import {
  useState,
  useEffect,
  ChangeEvent,
  MouseEventHandler,
  useRef,
} from 'react';

import { Navigate, useNavigate } from 'react-router-dom';
import { Transition } from 'react-transition-group';

import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import { login } from 'src/api';
import { Flexbox } from 'src/components/Alignments';
import Spinner from 'src/components/Spinner';
import { PathNames } from 'src/types/client/url';
import { ClientError } from 'src/utils/clientError';
import cookieHelper from 'src/utils/cookieHelper';
import { UrlUtil } from 'src/utils/url';

type StateProp = {
  state: string;
};

const duration = 750;

const PageWrapper = styled('div')(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  boxSizing: 'border-box',
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
  '& *': {
    fontSize: '14px',
  },
}));

const Logo = styled('h1')<StateProp>(
  ({ theme, state }) => `
  margin-bottom: 4rem;
  text-align: center;
  font-size: 2.5rem;
  font-weight: 400;
  color: ${theme.palette.text.primary};
  letter-spacing: 0.5px;
  transform: translate3d(0, ${state === 'exited' ? '-30px' : '0'}, 0);
  opacity: ${state === 'exited' ? 0 : 1};
  transition:
    opacity ${duration}ms ease-out,
    transform ${duration}ms ease-out;
`
);
const Form = styled('form')<StateProp>(
  ({ state }) => `
  transform: translate3d(0, ${state === 'exited' ? '-30px' : '0'}, 0);
  opacity: ${state === 'exited' ? 0 : 1};
  transition:
    opacity ${duration}ms ease-out,
    transform ${duration}ms ease-out;
  transition-delay: 600ms;
`
);

const LoginFooter = styled('div')<StateProp>(
  ({ theme, state }) => `
  transform: translate3d(0, ${state === 'exited' ? '-30px' : '0'}, 0);
  opacity: ${state === 'exited' ? 0 : 1};
  transition:
    opacity ${duration}ms ease-out,
    transform ${duration}ms ease-out;
  margin-top: 3rem;
  text-align: center;
  transition-delay: 600ms;
  p {
    margin: 6px 0;
    color: ${theme.palette.text.secondary};
  }
  a {
    color: ${theme.palette.text.secondary};
    text-decoration: none;
    &.email {
      color: ${theme.palette.primary.main};
    }
  }
`
);

const Notice = styled('p')<StateProp>(
  ({ theme, state }) => `
  transform: translate3d(0, ${state === 'exited' ? '-30px' : '0'}, 0);
  opacity: ${state === 'exited' ? 0 : 1};
  transition:
    opacity ${duration}ms ease-out,
    transform ${duration}ms ease-out;
  margin-top: 2rem;
  color: ${theme.palette.text.secondary};
  text-align: center;
  transition-delay: 600ms;
  em {
    color: ${theme.palette.text.primary};
    font-style: normal;
  }
`
);

const ErrorContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 1rem;
`;

// TODO: apply yup, react-hook-form
export default function Login(): JSX.Element {
  const [trigger, setTrigger] = useState(false);
  const nodeRef = useRef(null);

  const [usernameInput, setUsernameInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState();

  const navigate = useNavigate();

  useEffect(() => {
    setTrigger(true);
  }, []);

  const handleSubmit: MouseEventHandler<HTMLButtonElement> = async event => {
    event.preventDefault();
    if (isLoading) {
      return;
    }
    setIsLoading(true);
    try {
      await login({ username: usernameInput, password: passwordInput });
      navigate(UrlUtil.getUrl(PathNames.HOME), { replace: true });
    } catch (error) {
      const err = error as ClientError;

      const message = (() => {
        if (error instanceof ClientError) {
          return (
            err.originalError?.response.data?.detail ||
            err.originalError?.message ||
            err.message
          );
        }
        return err.message;
      })();

      setErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputUsername = (e: ChangeEvent<HTMLInputElement>) => {
    setUsernameInput(e.target.value);
    setErrorMessage(undefined);
  };

  const handleInputPassword = (e: ChangeEvent<HTMLInputElement>) => {
    setPasswordInput(e.target.value);
    setErrorMessage(undefined);
  };

  if (cookieHelper.hasAccessToken()) {
    return <Navigate to={UrlUtil.getUrl(PathNames.HOME)} />;
  }
  return (
    <PageWrapper>
      <Transition in={trigger} timeout={duration} nodeRef={nodeRef}>
        {state => (
          <div>
            <Logo state={state}>Lunit INSIGHT</Logo>

            <Form state={state}>
              <Flexbox $direction="column" style={{ gap: '0.5rem' }}>
                <TextField
                  id="username"
                  size="small"
                  variant="outlined"
                  placeholder="Username"
                  autoComplete="username"
                  value={usernameInput}
                  fullWidth
                  onChange={handleInputUsername}
                  error={Boolean(errorMessage)}
                />
                <TextField
                  id="password"
                  size="small"
                  variant="outlined"
                  type="password"
                  placeholder="Password"
                  autoComplete="current-password"
                  value={passwordInput}
                  fullWidth
                  onChange={handleInputPassword}
                  error={Boolean(errorMessage)}
                />
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={
                    !(
                      usernameInput &&
                      !errorMessage &&
                      passwordInput &&
                      !isLoading
                    )
                  }
                >
                  {isLoading ? <Spinner size={26} /> : 'Login'}
                </Button>
                <ErrorContainer>
                  {errorMessage && (
                    <Typography
                      variant="subtitle1"
                      component="div"
                      data-view="error"
                      color="error"
                    >
                      {errorMessage}
                    </Typography>
                  )}
                </ErrorContainer>
              </Flexbox>
              <Notice state={state}>
                Lunit CTL Office is optimized to run on the{' '}
                <em>Chrome browser</em>.
              </Notice>
            </Form>
            <LoginFooter state={state}>
              <p>Admin for Lunit CTL Office</p>
              <p>
                Contact{' '}
                <a className="email" href="mailto:contact@lunit.io">
                  contact@lunit.io
                </a>
              </p>
              <p>Powered by Lunit Inc. {new Date().getFullYear()}</p>
            </LoginFooter>
          </div>
        )}
      </Transition>
    </PageWrapper>
  );
}
